import {
  Button,
  Divider,
  Form,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Tabs,
} from "antd";
import DistributionPointSelected from "context/MyDistributionPoint";
import { useClient } from "hooks/useClient";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  downloadJson,
  downloadJsonReports,
  getJsonReportList,
  JsonReportView,
  markJsonReportAsSent,
  previewJson,
} from "api/generated";
import { useDateRange } from "context/DateRangeContext";
import { handleAxiosError } from "common/helpers/generalUtils";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import FormItem from "antd/es/form/FormItem";
import {
  DownloadOutlined,
  ExclamationCircleFilled,
  FileAddOutlined,
  ProfileOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import JsonReportsTable from "./components/JsonReportTable";
import { useTableSelection } from "hooks/commons/useTableSelection";
import CreateJsonReport, { REPORT_TYPE } from "./components/CreateJsonReport";
import {
  openJsonInModal,
  saveJsonToFile,
} from "routes/stockControl/components/jsonViewer";
import dayjs from "dayjs";
import LateArrivingInformationAlert from "routes/stockControl/components/LateArrivingInformationAlert";

function JsonReportsScreen(props: RouteComponentProps) {
  const searchParams = new URLSearchParams(props.location.search);
  const activeTab = searchParams.get("tab") || "day";
  const { client } = useClient();
  const selection = useTableSelection();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates({
    startDateFormat: "YYYY-MM-DD",
    endDateFormat: "YYYY-MM-DD",
  });
  const { startDate: startDateForLateInfo, endDate: endDateForLateInfo } =
    getFormattedDates();

  const [isLoading, setIsLoading] = useState(false);
  const [jsonList, setJsonList] = useState<JsonReportView[]>([]);
  const [isOpenCreator, setIsOpenCreator] = useState(false);
  // Referencias para los AbortController de cada petición
  const fetchJsonControllerRef = useRef<AbortController | null>(null);

  const [selectedReportType, setSelectedReportType] = useState<REPORT_TYPE>(
    activeTab === "day" ? REPORT_TYPE.DIARIO : REPORT_TYPE.MENSUAL,
  );
  const defaultType = useMemo(
    () =>
      selectedReportType === REPORT_TYPE.DIARIO
        ? DateFilterType.MONTH
        : DateFilterType.YEAR,
    [selectedReportType],
  );

  const reloadTable = useCallback(() => {
    if (!distributionPoint) {
      return;
    }
    // Cancelar petición anterior si existe
    if (fetchJsonControllerRef.current) {
      fetchJsonControllerRef.current.abort();
    }
    // Crear nuevo AbortController para esta petición
    const controller = new AbortController();
    fetchJsonControllerRef.current = controller;

    setIsLoading(true);
    getJsonReportList({
      client,
      body: {
        startDate,
        endDate,
        reportType: selectedReportType,
        distributionPointId: distributionPoint.id,
      },
      signal: controller.signal,
    })
      .then(({ data }) => {
        setJsonList(data ?? []);
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible obtener los reportes json"),
      )
      .finally(() => {
        setIsLoading(false);
      });
  }, [client, startDate, endDate, selectedReportType, distributionPoint]);

  // useEffect principal
  useEffect(() => {
    reloadTable();
  }, [reloadTable]);

  useEffect(() => {
    selection.resetSelected();
  }, [distributionPoint?.id, startDate, endDate]);

  function handleUpdateTabFilter(key: "day" | "month") {
    props.history.push(`?tab=${key}`);
    setJsonList([]);
    setSelectedReportType(
      key === "day" ? REPORT_TYPE.DIARIO : REPORT_TYPE.MENSUAL,
    );
  }

  function handleDownloadZip() {
    if (!distributionPoint || isLoading) {
      return;
    }
    setIsLoading(true);

    const reportNames =
      jsonList
        .filter((item) => selection.isSelected(item.idReport ?? "invalid"))
        .flatMap((item) => item.reportName ?? "invalid") ?? [];

    const zipName = `reportes_json_${selectedReportType}_${distributionPoint.name}.zip`;

    downloadJsonReports({
      client,
      body: {
        reportNames,
      },
    })
      .then(({ data }) => {
        saveJsonToFile(data, "application/zip", zipName);
        selection.resetSelected();
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible descargar los reportes json"),
      )
      .finally(() => {
        setIsLoading(false);
      });
  }

  function handleDownloadJsonReport(reportName: string) {
    if (!distributionPoint || isLoading) {
      return;
    }
    setIsLoading(true);
    downloadJson({
      client,
      body: {
        reportName,
        distributionPointId: distributionPoint.id,
      },
    })
      .then(({ data }) => {
        saveJsonToFile(data, "application/json", reportName);
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible descargar el reporte json"),
      )
      .finally(() => {
        setIsLoading(false);
      });
  }
  function handlePreviewJsonReport(reportName: string) {
    if (!distributionPoint || isLoading) {
      return;
    }
    setIsLoading(true);
    previewJson({
      client,
      body: {
        reportName,
        distributionPointId: distributionPoint.id,
      },
    })
      .then(({ data }) => {
        if (data) {
          openJsonInModal(data, "showErrors");
        }
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible obtener el reporte json"),
      )
      .finally(() => {
        setIsLoading(false);
      });
  }

  function handleToggleSentToSAT(checked: boolean, record: JsonReportView) {
    setIsLoading(true);
    markJsonReportAsSent({
      client,
      path: {
        idJsonReport: record.idReport ?? "",
        sent: checked,
      },
    })
      .then(({ data }) => {
        message.success(data);
        reloadTable();
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible descargar el reporte json"),
      )
      .finally(() => {
        setIsLoading(false);
      });
  }

  function showConfirmSentToSAT(checked: boolean, record: JsonReportView) {
    const checkedString = checked ? "Enviado al SAT" : "No enviado al SAT";
    Modal.confirm({
      title: `¿Quieres marcar como ${checkedString} este reporte Json?`,
      icon: <ExclamationCircleFilled />,
      width: "65%",
      content: (
        <div>
          <p>
            ¿Estas seguro de querer marcar este reporte Json como{" "}
            {checkedString}?
          </p>
          {checked && (
            <>
              <p>
                Una vez confirmado este cambio, muchas funciones respecto al
                control de existencias no estarán disponibles para este mes:{" "}
                {dayjs(record.creationDate).format("MMMM [de] YYYY")}
              </p>
              <div>
                <ul>
                  <li>
                    El proceso para re-calcular existencias no estará disponible
                  </li>
                  <li>
                    El proceso de regeneración de reportes Json para el mes en
                    cuestión no estará disponible
                  </li>
                  <li>
                    Sistemas externos a Control Volumétrico podría dejar de
                    permitir actualización de datos para este periodo
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      ),
      okButtonProps: {
        style: { whiteSpace: "pre-wrap", minBlockSize: "fit-content" },
      },
      okText: "Estoy seguro de querer cambiar el estatus del reporte Json",
      cancelText: "Cancelar",
      onOk() {
        handleToggleSentToSAT(checked, record);
      },
      onCancel() {
        reloadTable();
      },
    });
  }

  return (
    <>
      <Spin size="large" tip="Cargando..." spinning={isLoading}>
        <Form layout="vertical">
          <Row align="bottom" gutter={24}>
            <ResponsiveCol size="xl">
              <FormItem
                label="Por favor selecciona el tipo de reporte a consultar"
                required
              >
                <Tabs
                  activeKey={activeTab}
                  items={[
                    {
                      key: "day",
                      label: "Json Diarios",
                      icon: <ProfileOutlined />,
                    },
                    {
                      key: "month",
                      label: "Json Mensuales",
                      icon: <ReadOutlined />,
                    },
                  ]}
                  onChange={(key) => {
                    handleUpdateTabFilter(key as "day" | "month");
                  }}
                />
              </FormItem>
            </ResponsiveCol>

            <ResponsiveCol>
              <FormItem label="Seleccionar periodo" required>
                <DateRangePicker
                  config={{
                    allowedTypes: [defaultType],
                    defaultType,
                    forceType: defaultType,
                    showQuickFilters: true,
                  }}
                  rangeTimeValue={rangeTimeValue}
                  setRangeTimeValue={setRangeTimeValue}
                />
              </FormItem>
            </ResponsiveCol>

            <ResponsiveCol size="lg">
              <FormItem label="Periodo seleccionado:">
                <div style={{ display: "flex", gap: "12px" }}>
                  <span>
                    <strong>Desde:</strong> {startDate}
                  </span>
                  <span>
                    <strong>Hasta:</strong> {endDate}
                  </span>
                </div>
              </FormItem>
            </ResponsiveCol>

            <ResponsiveCol size="xl">
              {distributionPoint?.id && (
                <LateArrivingInformationAlert
                  isLoading={isLoading}
                  onReload={() => {
                    reloadTable();
                  }}
                  startDate={startDateForLateInfo}
                  endDate={endDateForLateInfo}
                  distributionPointIds={[distributionPoint.id]}
                />
              )}
              <Divider />
            </ResponsiveCol>

            <ResponsiveCol size="xl">
              <Space
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <Button
                  type="primary"
                  onClick={() => {
                    setIsOpenCreator(true);
                  }}
                  icon={<FileAddOutlined />}
                >
                  Generar Reporte CV (JSON)
                </Button>
                <Button
                  type="default"
                  icon={<DownloadOutlined />}
                  disabled={!selection.anySelected}
                  onClick={handleDownloadZip}
                >
                  Descargar {selection.countSelected} reportes seleccionados
                </Button>
              </Space>
            </ResponsiveCol>

            <Divider style={{ marginTop: "10px" }} />

            <ResponsiveCol size="xl">
              <JsonReportsTable
                data={jsonList}
                loading={isLoading}
                mode="edit"
                selection={selection}
                onDownload={handleDownloadJsonReport}
                onToggleSent={showConfirmSentToSAT}
                onView={handlePreviewJsonReport}
                statusConfig={
                  defaultType === DateFilterType.MONTH
                    ? {
                        render: false,
                      }
                    : undefined
                }
              />
            </ResponsiveCol>
          </Row>
          <Divider />
        </Form>
      </Spin>
      {isOpenCreator && (
        <CreateJsonReport
          onClose={(json) => {
            setIsOpenCreator(false);
            if (json) {
              reloadTable();
              openJsonInModal(
                json,
                "showErrors",
                `Json Report - ${json.reportName}`,
              );
            }
          }}
          selectedReportType={selectedReportType}
          setSelectedReportType={(value) => {
            const key = value === REPORT_TYPE.DIARIO ? "day" : "month";
            handleUpdateTabFilter(key);
          }}
          defaultDates={rangeTimeValue}
          updateDates={(dates) => {
            // Tomamos solo la primera fecha (la que seleccionó el usuario)
            const [selectedDate] = dates;

            if (selectedReportType === REPORT_TYPE.DIARIO) {
              setRangeTimeValue([
                selectedDate.startOf("month"),
                selectedDate.endOf("month"),
              ]);
            } else {
              setRangeTimeValue([
                selectedDate.startOf("year"),
                selectedDate.endOf("year"),
              ]);
            }
          }}
        />
      )}
    </>
  );
}

export default withRouter(JsonReportsScreen);
